import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { auth } from "../services/supabase";
import { API_BASE_URL } from "../frontend_config";
import {
  UserRound,
  Stethoscope,
  AlertCircle,
  RotateCcw,
  Send,
  Square,
  Play,
  ClipboardList,
} from "lucide-react";

const MODULE = "module_2_patient_sim";

export default function PatientSimulator() {
  const [sessionId, setSessionId] = useState(null);
  const [state, setState] = useState("idle");
  const [requiredFields, setRequiredFields] = useState(["complaint"]);

  const [complaint, setComplaint] = useState("");
  const [runEvaluatorOnEnd, setRunEvaluatorOnEnd] = useState(true);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);
  const chatEndRef = useRef(null);
  const focusInput = () => setTimeout(() => inputRef.current?.focus(), 0);

  const canStart = useMemo(() => !loading && state !== "running", [loading, state]);
  const canChat = useMemo(() => !!sessionId && state === "running" && !loading, [sessionId, state, loading]);

  useEffect(() => {
    if (state === "running") focusInput();
  }, [state]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function getAuthHeader() {
    const { data, error } = await auth.getCurrentSession();
    if (error) return {};
    const token = data?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async function apiFetch(url, options = {}) {
    const authHeader = await getAuthHeader();
    const headers = { ...(options.headers || {}), ...authHeader };
    return fetch(url, { ...options, headers });
  }

  async function createSession() {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ module: MODULE }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function initSession(id, complaintText) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload: { complaint: complaintText } }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function sendTurn(id, userMessage) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/turn`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_message: userMessage }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function endSession(id) {
    const res = await apiFetch(`${API_BASE_URL}/api/sessions/${id}/end`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload: { run_evaluator: runEvaluatorOnEnd } }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async function onStart() {
    const c = (complaint || "").trim();
    if (!c) {
      setMessages([{ role: "system", content: "Please enter a chief complaint to start." }]);
      return;
    }

    setLoading(true);
    try {
      setMessages([]);
      setInput("");
      setSessionId(null);
      setState("idle");

      const created = await createSession();
      setSessionId(created.session_id);
      setState(created.state || "needs_initialization");
      setRequiredFields(created.required_fields || ["complaint"]);

      const init = await initSession(created.session_id, c);
      setState(init.state || "running");

      setMessages([{ role: "assistant", content: init.assistant_message }]);
      focusInput();
    } catch (e) {
      console.error(e);
      setMessages([{ role: "system", content: `Failed to start: ${String(e)}` }]);
      setState("idle");
      setSessionId(null);
    } finally {
      setLoading(false);
    }
  }

  async function onSend() {
    const text = input.trim();
    if (!text || !canChat) return;

    setInput("");
    setLoading(true);
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      const out = await sendTurn(sessionId, text);
      setState(out.state || "running");

      if (out.assistant_message) {
        setMessages((prev) => [...prev, { role: "assistant", content: out.assistant_message }]);
      }

      focusInput();
    } catch (e) {
      console.error(e);
      setMessages((prev) => [...prev, { role: "system", content: `Turn failed: ${String(e)}` }]);
      focusInput();
    } finally {
      setLoading(false);
    }
  }

  async function onEnd() {
    if (!sessionId) return;
    setLoading(true);
    try {
      const out = await endSession(sessionId);
      setState(out.state || "completed");

      if (out?.evaluator?.text) {
        setMessages((prev) => [...prev, { role: "system", content: `DEBRIEF\n\n${out.evaluator.text}` }]);
      } else if (out?.assistant_message) {
        setMessages((prev) => [...prev, { role: "system", content: out.assistant_message }]);
      }
    } catch (e) {
      console.error(e);
      setMessages((prev) => [...prev, { role: "system", content: `End failed: ${String(e)}` }]);
    } finally {
      setLoading(false);
    }
  }

  function onReset() {
    setSessionId(null);
    setState("idle");
    setRequiredFields(["complaint"]);
    setMessages([]);
    setComplaint("");
    setInput("");
    setLoading(false);
  }

  const onComplaintKeyDown = (e) => {
    const isCtrlEnter = (e.ctrlKey || e.metaKey) && e.key === "Enter";
    if (!isCtrlEnter) return;

    e.preventDefault();

    const c = (complaint || "").trim();
    if (!c) return;
    if (!canStart) return;

    onStart();
  };

  const renderRoleIcon = (role) => {
    if (role === "user") return <UserRound size={14} />;
    if (role === "assistant") return <Stethoscope size={14} />;
    return <AlertCircle size={14} />;
  };

  const renderRoleLabel = (role) => {
    if (role === "user") return "You";
    if (role === "assistant") return "Doctor";
    return "System";
  };

  const renderRoleClass = (role) => {
    if (role === "user") return "role-user";
    if (role === "assistant") return "role-assistant";
    return "role-system";
  };

  return (
    <div className="patient-sim">
      {/* Header */}
      <div className="sim-header">
        <h2>Patient Simulator</h2>
        <p className="sim-subtitle">
          You are the patient. The AI is the doctor. Start by entering your chief complaint.
        </p>
      </div>

      {/* Status Bar */}
      <div className="sim-status-bar">
        <span className="status-label">
          State: <strong>{state}</strong>
        </span>
        {state === "needs_initialization" && requiredFields?.length ? (
          <span className="status-label">
            needs: {requiredFields.join(", ")}
          </span>
        ) : null}
        <div className="status-actions">
          <button className="btn btn-ghost" onClick={onReset} disabled={loading}>
            <RotateCcw size={14} />
            Reset
          </button>
        </div>
      </div>

      {/* Setup Panel */}
      {state !== "running" && (
        <div className="setup-panel">
          <div className="setup-title">
            <ClipboardList size={16} />
            Scenario setup
          </div>

          <div className="field-group">
            <label htmlFor="ps-complaint">Chief complaint</label>
            <div className="field-hint">
              Describe your main symptom with as few words as possible (e.g. Dry cough). Tip: Ctrl/Cmd+Enter to start.
            </div>
            <textarea
              id="ps-complaint"
              value={complaint}
              onChange={(e) => setComplaint(e.target.value)}
              onKeyDown={onComplaintKeyDown}
              rows={3}
              placeholder="e.g., I have chest pain and shortness of breath..."
              disabled={loading}
            />
          </div>

          <div className="setup-actions">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={runEvaluatorOnEnd}
                onChange={(e) => setRunEvaluatorOnEnd(e.target.checked)}
                disabled={loading}
              />
              Run debrief on end
            </label>

            <button className="btn btn-primary" onClick={onStart} disabled={!canStart}>
              <Play size={14} />
              {loading ? "Starting..." : "Start session"}
            </button>
          </div>

          {/* Messages shown during setup (e.g. validation errors) */}
          {messages.length > 0 && (
            <div className="chat-area" style={{ marginTop: 'var(--space-4, 16px)', minHeight: 'auto' }}>
              {messages.map((m, idx) => (
                <div key={idx} className="chat-message">
                  <div className={`msg-role ${renderRoleClass(m.role)}`}>
                    {renderRoleIcon(m.role)}
                    {renderRoleLabel(m.role)}
                  </div>
                  <div className="msg-body">
                    <ReactMarkdown>{String(m.content || "")}</ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chat Area */}
      {state === "running" && (
        <div className="chat-area">
          {messages.map((m, idx) => (
            <div key={idx} className="chat-message">
              <div className={`msg-role ${renderRoleClass(m.role)}`}>
                {renderRoleIcon(m.role)}
                {renderRoleLabel(m.role)}
              </div>
              <div className="msg-body">
                <ReactMarkdown>{String(m.content || "")}</ReactMarkdown>
              </div>
            </div>
          ))}
          {loading && (
            <div className="chat-message">
              <div className="msg-role role-assistant">
                <Stethoscope size={14} />
                Doctor
              </div>
              <div className="msg-body" style={{ opacity: 0.5 }}>Thinking...</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
      )}

      {/* Input Bar */}
      {state === "running" && (
        <div className="chat-input-bar">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Type your patient response..."
            disabled={!canChat}
            rows={1}
          />
          <button className="btn btn-primary" onClick={onSend} disabled={!canChat || !input.trim()}>
            <Send size={14} />
            {loading ? "Sending..." : "Send"}
          </button>
          <button className="btn btn-secondary" onClick={onEnd} disabled={loading}>
            <Square size={14} />
            End
          </button>
        </div>
      )}
    </div>
  );
}
